export type ToastVariant = "success" | "danger" | "warning" | "info";
export type ToastTheme = "light" | "dark";

interface ToastThemePalette {
    headerBackground: string;
    headerText: string;
    metaText: string;
    messageBackground: Record<ToastVariant, string>;
    messageText: Record<ToastVariant, string>;
    shadow: string;
}

const TOAST_PALETTES: Record<ToastTheme, ToastThemePalette> = {
    light: {
        headerBackground: "#FFFFFF",
        headerText: "#1E1E1E",
        metaText: "#6B7280",
        messageBackground: {
            success: "#059A2C",
            danger: "#DC3545",
            info: "#0A91B1",
            warning: "#F4CF39",
        },
        messageText: {
            success: "#FFFFFF",
            danger: "#FFFFFF",
            info: "#FFFFFF",
            warning: "#1E1E1E",
        },
        shadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    },
    dark: {
        headerBackground: "#1E1E1E",
        headerText: "#F5F5F5",
        metaText: "#B0B0B0",
        messageBackground: {
            success: "#047A23",
            danger: "#B02A37",
            info: "#044654",
            warning: "#987D01",
        },
        messageText: {
            success: "#F5F5F5",
            danger: "#F5F5F5",
            info: "#F5F5F5",
            warning: "#F5F5F5",
        },
        shadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
    },
};

export function resolveToastThemeFromDom(
    doc: Document | null = typeof document !== "undefined" ? document : null
): ToastTheme {
    if (!doc) {
        return "light";
    }

    const bodyTheme =
        doc.body?.getAttribute("data-bs-theme") ||
        doc.body?.getAttribute("data-theme");
    const rootTheme =
        doc.documentElement?.getAttribute("data-bs-theme") ||
        doc.documentElement?.getAttribute("data-theme");
    const resolvedTheme = bodyTheme || rootTheme || "light";

    return resolvedTheme === "dark" ? "dark" : "light";
}

export interface ToastColors {
    headerBackground: string;
    headerText: string;
    messageBackground: string;
    messageText: string;
    metaText: string;
    shadow: string;
}

export function normalizeToastVariant(
    variant: string | null | undefined
): ToastVariant {
    if (!variant) {
        return "success";
    }

    const normalized = variant.trim().toLowerCase();
    if (normalized === "success") return "success";
    if (normalized === "danger" || normalized === "error") return "danger";
    if (normalized === "warning" || normalized === "warn") return "warning";
    if (normalized === "info" || normalized === "information") return "info";

    return "success";
}

export function getToastColors(
    variant: ToastVariant | string,
    theme: ToastTheme | string
): ToastColors {
    const safeTheme: ToastTheme = theme === "dark" ? "dark" : "light";
    const safeVariant = normalizeToastVariant(variant);
    const palette = TOAST_PALETTES[safeTheme];

    return {
        headerBackground: palette.headerBackground,
        headerText: palette.headerText,
        messageBackground:
            palette.messageBackground[safeVariant] ||
            TOAST_PALETTES.dark.messageBackground.success,
        messageText:
            palette.messageText[safeVariant] ||
            TOAST_PALETTES.dark.messageText.success,
        metaText: palette.metaText,
        shadow: palette.shadow,
    };
}
